import React from 'react';
import {
  Box,
  IconButton,
  Typography,
  Toolbar,
  AppBar,
  Tooltip,
  Badge,
} from '@mui/material';
import {
  Search as KeywordIcon,
  Speed as RankingOnDemandIcon,
  AutoAwesome as GenerativeSearchIcon,
  Schema as SchemaIcon,
  AutoAwesome as SparkleIcon,
  AutoFixHigh as MagicIcon,
  Logout as LogoutIcon,
} from '@mui/icons-material';

interface TopNavigationProps {
  activeSection: string;
  onSectionChange: (section: string) => void;
  notificationCount?: number;
  onNotificationClick?: () => void;
  onLogout?: () => void;
}

const TopNavigation: React.FC<TopNavigationProps> = ({
  activeSection,
  onSectionChange,
  notificationCount = 0,
  onNotificationClick,
  onLogout
}) => {
  // Simplified navigation - unified tools
  const navigationItems = [
    { id: 'performance-hub', label: 'Performance Hub', icon: <RankingOnDemandIcon /> },
    { id: 'keyword-intelligence', label: 'Keyword Intelligence', icon: <KeywordIcon /> },
    { id: 'content-studio', label: 'Content Studio', icon: <MagicIcon /> },
    { id: 'schema-generator', label: 'Schema Generator', icon: <SchemaIcon /> },
    { id: 'generative-search', label: 'Generative Search', icon: <GenerativeSearchIcon /> },
  ];

  return (
    <AppBar 
      position="static" 
      sx={{ 
        bgcolor: 'white', 
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
        borderBottom: '1px solid #f0f0f0'
      }}
    >
      <Toolbar sx={{ justifyContent: 'space-between', minHeight: '64px' }}>
        {/* Logo/Brand on the left */}
        <Typography variant="h6" sx={{ fontWeight: 700, color: '#cc081c' }}>
          🦄 Unicorn SEO
        </Typography>
        
        {/* Navigation items in the center */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 0.5, 
          overflowX: 'auto',
          flex: 1,
          justifyContent: 'center',
          mx: 2,
          '&::-webkit-scrollbar': {
            height: '4px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#ccc',
            borderRadius: '2px',
          }
        }}>
          {navigationItems.map((item) => (
            <Tooltip key={item.id} title={item.label} arrow>
              <IconButton
                onClick={() => onSectionChange(item.id)}
                sx={{
                  color: activeSection === item.id ? '#cc081c' : '#666',
                  bgcolor: activeSection === item.id ? 'rgba(123, 97, 255, 0.1)' : 'transparent',
                  borderRadius: '8px',
                  padding: '6px',
                  minWidth: '40px',
                  '&:hover': {
                    bgcolor: activeSection === item.id ? 'rgba(123, 97, 255, 0.15)' : 'rgba(0,0,0,0.05)',
                  }
                }}
              >
                {item.icon}
              </IconButton>
            </Tooltip>
          ))}
        </Box>
        
        {/* Sparkle icon and logout on the right */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title={notificationCount > 0 ? `${notificationCount} new updates` : 'What\'s new'} arrow>
            <IconButton
              onClick={onNotificationClick}
              sx={{
                color: '#cc081c',
                bgcolor: notificationCount > 0 ? 'rgba(123, 97, 255, 0.1)' : 'transparent',
                '&:hover': {
                  bgcolor: 'rgba(123, 97, 255, 0.15)',
                },
                '@keyframes sparkle': {
                  '0%': { transform: 'scale(1) rotate(0deg)' },
                  '50%': { transform: 'scale(1.1) rotate(180deg)' },
                  '100%': { transform: 'scale(1) rotate(360deg)' }
                },
                animation: notificationCount > 0 ? 'sparkle 2s infinite' : 'none',
              }}
            >
              <Badge
                badgeContent={notificationCount}
                color="secondary"
                sx={{
                  '& .MuiBadge-badge': {
                    bgcolor: '#ff4757',
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '0.7rem',
                  }
                }}
              >
                <SparkleIcon />
              </Badge>
            </IconButton>
          </Tooltip>
          {onLogout && (
            <Tooltip title="Logout" arrow>
              <IconButton
                onClick={onLogout}
                sx={{
                  color: '#666',
                  '&:hover': {
                    bgcolor: 'rgba(0,0,0,0.05)',
                    color: '#ff4757',
                  }
                }}
              >
                <LogoutIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default TopNavigation;